import React, { Component } from 'react';
import styles from '../../styles/amcs.scss';
import { SubHeading } from '../common';
import DonutChart from '../common/donut/donutChart';

interface AssetAllocation {
  assetAllocEquity?: number | null;
  assetAllocDebt?: number | null;
  assetAllocCash?: number | null;
  equityAllocLargeCap?: number | null;
  equityAllocMidCap?: number | null;
  equityAllocSmallCap?: number | null;
  debtAllocGovt?: number | null;
  debtAllocMunicipal?: number | null;
  debtAllocCorporate?: number | null;
  debtAllocSecuritized?: number | null;
}

interface FundDistributionProps {
  assetAllocation: AssetAllocation | null | undefined; // Allow null/undefined top level prop
}

interface FundDistributionState {
  selectedTab: 'equity' | 'debt' | 'others';
}

class FundDistribution extends Component<FundDistributionProps> {
  state: FundDistributionState = {
    selectedTab: (() => {
      const propsAA = this.props.assetAllocation;
      const equity = (propsAA && propsAA.assetAllocEquity) || 0;
      const debt = (propsAA && propsAA.assetAllocDebt) || 0;
      const cash = (propsAA && propsAA.assetAllocCash) || 0;

      if (equity >= debt && equity >= cash) {
        return 'equity';
      } else if (debt >= equity && debt >= cash) {
        return 'debt';
      } else {
        return 'others';
      }
    })(),
  };

  switchBtn = (tab: 'equity' | 'debt' | 'others') => {
    this.setState({ selectedTab: tab });
  };

  // Update round2 to accept null/undefined directly
  private round2 = (n?: number | null): number => {
    const v = n ? n : 0;
    return parseFloat(v.toFixed(2));
  };

  public tabSelector = () => {
    const { assetAllocation } = this.props;
    const { selectedTab } = this.state;
    return (
      <div className="lil-flex lil-justify-left lil-pt-6">
        <div
          id="switchBtnHolder1"
          onClick={() => this.switchBtn('equity')}
          className={`lil-w-1/5 lil-text-center lil-cursor-pointer lil-font-semibold lil-py-2 sm:lil-w-1/2
                                ${
                                  selectedTab === 'equity'
                                    ? ` lil-border-b-lg ${styles['border-blue-250']} lil-bg-blue-50 `
                                    : ' '
                                }
                                ${
                                  this.round2(
                                    assetAllocation.assetAllocEquity
                                  ) === 0
                                    ? ' lil-hidden '
                                    : ''
                                }`}
        >
          <div>Equity</div>
          <div className="lil-font-thin">
            {this.round2(assetAllocation.assetAllocEquity)}%
          </div>
        </div>
        <div
          id="switchBtnHolder2"
          onClick={() => this.switchBtn('debt')}
          className={`lil-w-1/5 lil-text-center lil-cursor-pointer lil-font-semibold lil-py-2 sm:lil-w-1/2
                                ${
                                  selectedTab === 'debt'
                                    ? `lil-border-b-lg ${styles['border-blue-250']} lil-bg-blue-50 `
                                    : ' '
                                }
                                ${
                                  this.round2(
                                    assetAllocation.assetAllocDebt
                                  ) === 0
                                    ? ' lil-hidden '
                                    : ''
                                }`}
        >
          <div>Debt</div>
          <div className="lil-font-thin">
            {this.round2(assetAllocation.assetAllocDebt)}%
          </div>
        </div>
        <div
          id="switchBtnHolder3"
          onClick={() => this.switchBtn('others')}
          className={`lil-w-1/5 lil-text-center lil-cursor-pointer lil-font-semibold lil-py-2 sm:lil-w-1/2
                                ${
                                  selectedTab === 'others'
                                    ? `lil-border-b-lg ${styles['border-blue-250']} lil-bg-blue-50 `
                                    : ' '
                                }
                                ${
                                  this.round2(
                                    // Rely on round2 to handle nulls for individual props here
                                    100 -
                                      (assetAllocation.assetAllocEquity || 0) - // Use || 0 directly
                                      (assetAllocation.assetAllocDebt || 0) // Use || 0 directly
                                  ) === 0
                                    ? ' lil-hidden '
                                    : ''
                                }`}
        >
          <div>Others</div>
          <div className="lil-font-thin">
            {this.round2(
              // Rely on round2 to handle nulls for individual props here
              100 -
                (assetAllocation.assetAllocEquity || 0) - // Use || 0 directly
                (assetAllocation.assetAllocDebt || 0) // Use || 0 directly
            )}
            %
          </div>
        </div>
      </div>
    );
  };

  render() {
    const { assetAllocation } = this.props;

    // Return null early if assetAllocation is not provided
    if (!assetAllocation) {
      return null;
    }

    const { selectedTab } = this.state;
    // console.log('asset allocation is: ', assetAllocation); // Removed console.log
    const equityData = [
      {
        name: 'Large Cap', // Corrected space
        y: this.round2(assetAllocation.equityAllocLargeCap),
        color: '#14407C',
      },
      {
        name: 'Mid Cap', // Corrected space
        y: this.round2(assetAllocation.equityAllocMidCap),
        color: '#1572ED',
      },
      {
        name: 'Small Cap', // Corrected space
        y: this.round2(assetAllocation.equityAllocSmallCap),
        color: '#15C5ED',
      },
    ];

    const debtData = [
      {
        name: 'Government Bonds',
        y: this.round2(
          // Rely on round2 to handle nulls for individual props here
          (assetAllocation.debtAllocGovt || 0) + // Use || 0 directly
            (assetAllocation.debtAllocMunicipal || 0) // Use || 0 directly
        ),
        color: '#14407C',
      },
      {
        name: 'Corporate Bonds',
        y: this.round2(
          // Rely on round2 to handle nulls for individual props here
          (assetAllocation.debtAllocCorporate || 0) + // Use || 0 directly
            (assetAllocation.debtAllocSecuritized || 0) // Use || 0 directly
        ),
        color: '#1572ED',
      },
    ];

    const otherData = [
      {
        name: 'Cash',
        y: this.round2(assetAllocation.assetAllocCash),
        color: '#14407C',
      },
      {
        name: 'Others',
        y: this.round2(
          // Rely on round2 to handle nulls for individual props here
          100 -
            (assetAllocation.assetAllocEquity || 0) - // Use || 0 directly
            (assetAllocation.assetAllocDebt || 0) - // Use || 0 directly
            (assetAllocation.assetAllocCash || 0) // Use || 0 directly
        ),
        color: '#1572ED',
      },
    ];
    let chartData = [];
    if (selectedTab === 'equity') {
      chartData = equityData;
    } else if (selectedTab === 'debt') {
      chartData = debtData;
    } else {
      chartData = otherData;
    }

    return (
      <div className="lil-mt-5 lil-border-b-md lil-border-grey-300  sm:lil-border-none">
        <SubHeading title="Fund Distribution" />
        {this.tabSelector()}
        <DonutChart key={selectedTab} data={chartData} showShadow={false} />
      </div>
    );
  }
}

export default FundDistribution;



// WEBPACK FOOTER //
// ./components/singleFundNewCmp/fundDistribution.tsx